import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: ReactNode;
  iconRight?: ReactNode;
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, icon, iconRight, invalid, ...props }, ref) => (
    <div className={cn('relative', className?.includes('w-full') && 'w-full')}>
      {icon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none">{icon}</span>
      )}
      <input
        ref={ref}
        className={cn(
          'w-full h-9 px-3 text-sm rounded-lg bg-bg-elev border border-border',
          'placeholder:text-fg-subtle text-fg',
          'transition-all duration-150',
          'focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:outline-none',
          icon && 'pl-9',
          iconRight && 'pr-9',
          invalid && 'border-danger focus-visible:ring-danger/30',
          className,
        )}
        {...props}
      />
      {iconRight && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none">{iconRight}</span>
      )}
    </div>
  ),
);
Input.displayName = 'Input';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  mono?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, mono, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        'w-full px-3 py-2.5 text-sm rounded-lg bg-bg-elev border border-border',
        'placeholder:text-fg-subtle text-fg resize-y',
        'transition-all duration-150',
        'focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:outline-none',
        mono && 'font-mono text-[13px] leading-relaxed',
        invalid && 'border-danger focus-visible:ring-danger/30',
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';

interface LabelProps {
  children: ReactNode;
  className?: string;
  required?: boolean;
  hint?: ReactNode;
  htmlFor?: string;
}

export function Label({ children, className, required, hint, htmlFor }: LabelProps) {
  return (
    <label htmlFor={htmlFor} className={cn('flex items-center justify-between text-xs font-medium text-fg-muted mb-1.5', className)}>
      <span>
        {children}
        {required && <span className="text-danger ml-0.5">*</span>}
      </span>
      {hint && <span className="text-fg-subtle font-normal">{hint}</span>}
    </label>
  );
}

interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function Field({ label, hint, error, required, children, className }: FieldProps) {
  return (
    <div className={cn('space-y-0', className)}>
      {label && <Label required={required} hint={hint}>{label}</Label>}
      {children}
      {error && <div className="text-xs text-danger mt-1">{error}</div>}
    </div>
  );
}
